'use client';

import { type ComponentPropsWithoutRef, forwardRef, memo, type ReactNode } from 'react';

import { tailwindVariants, type VariantProps } from '@air/utils-tailwind';

export const iconButton = tailwindVariants({
  base: 'flex items-center justify-center rounded transition-colors focus:outline-none focus:ring-2 focus:ring-brand-9 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent',
  defaultVariants: {
    appearance: 'ghost',
    size: 'medium',
  },
  variants: {
    appearance: {
      filled: 'bg-neutral-12/80 text-white hover:bg-neutral-12',
      ghost: 'text-neutral-10 hover:bg-neutral-3 hover:text-neutral-12',
      outline:
        'border border-neutral-6 bg-white text-neutral-12 hover:bg-neutral-2 dark:border-neutral-7 dark:bg-neutral-2 dark:hover:bg-neutral-3',
    },
    size: {
      large: 'size-10',
      medium: 'size-8',
      small: 'size-6',
    },
  },
});

export type IconButtonVariants = VariantProps<typeof iconButton>;

export type IconButtonProps = IconButtonVariants &
  Omit<ComponentPropsWithoutRef<'button'>, 'children'> & {
    /** The icon to display */
    children: ReactNode;
    /** Accessible label for the button (required for icon-only buttons) */
    'aria-label': string;
  };

export const IconButton = memo(
  forwardRef<HTMLButtonElement, IconButtonProps>(
    ({ appearance, children, className, size, type = 'button', ...restOfProps }, forwardedRef) => {
      return (
        <button className={iconButton({ appearance, className, size })} type={type} {...restOfProps} ref={forwardedRef}>
          {children}
        </button>
      );
    },
  ),
);

IconButton.displayName = 'IconButton';
